import { AudioStorage } from "./AudioStorage";
import { decodeMp3ToPcmBuffer } from "./streaming/mp3/decodeMp3";
import { MpegAudioIndex } from "./streaming/mp3/MpegAudioIndex";
import { decodeOpusToPcmBuffer } from "./streaming/opus/decodeOpus";
import { OggOpusIndex } from "./streaming/opus/OggOpusIndex";
import { WaveformData } from "./types";
import { calculateWaveform } from "./calculateWaveform";

/**
 * Input data for loading a region from storage.
 */
export interface RegionLoadInput {
  id: string;
  audioBlob?: Blob;
  codec?: string | null;
  duration?: number;
  key: string;
  waveformKey: string | null;
}

/**
 * Result of loading a single region.
 */
export interface RegionLoadResult {
  id: string;
  audioBlob: Blob;
  duration?: number;
  preDecodedPcm?: Float32Array;
  waveform: WaveformData;
}

/**
 * Region loader for the streaming playback backend.
 *
 * MP3 regions are pre-decoded into 48 kHz stereo interleaved PCM on the main
 * thread (with periodic yields to avoid blocking the UI) so the worker
 * receives ready-to-play samples.  Opus regions only need container-level
 * duration parsing — their incremental decode happens in the worker.
 *
 * When no cached waveform exists, the region is fully decoded to PCM on the
 * main thread so the waveform can be calculated without `decodeAudioData()`.
 *
 * When loading multiple regions via `loadAll()`, the progress callback
 * is debounced internally via `queueMicrotask` so that many regions
 * resolving on the same tick produce a single callback invocation.
 */
export class RegionLoader {
  private audioStorage: AudioStorage;

  constructor(audioStorage: AudioStorage) {
    this.audioStorage = audioStorage;
  }

  /**
   * Load a single region from a storage key.
   * Fetches the audio blob, derives duration from the container, and
   * pre-decodes MP3 regions into interleaved PCM.
   */
  async loadFromKey(
    id: string,
    key: string,
    waveformKey: string | null,
    audioBlob?: Blob,
    options?: Pick<RegionLoadInput, "codec" | "duration">,
  ): Promise<RegionLoadResult> {
    const blob = audioBlob ?? await this.audioStorage.getAudio(id, key);
    const arrayBuffer = await blob.arrayBuffer();
    const data = new Uint8Array(arrayBuffer);

    let duration = options?.duration;
    let preDecodedPcm: Float32Array | undefined;
    let opusPcmForWaveform: Float32Array | undefined;

    if (options?.codec === "mp3") {
      const index = new MpegAudioIndex(data);
      preDecodedPcm = await decodeMp3ToPcmBuffer(index, true);
      duration = index.durationSeconds;
    } else if (options?.codec === "opus") {
      const index = new OggOpusIndex(data);
      duration = index.durationSeconds;

      // Only decode Opus to PCM when we need to calculate a waveform from
      // scratch.  This is a temporary decode — the worker handles Opus
      // incrementally during playback, so we discard the PCM after.
      if (waveformKey === null) {
        opusPcmForWaveform = await decodeOpusToPcmBuffer(index, true);
      }
    }

    const waveform = await this.audioStorage.getWaveform(
      id,
      waveformKey,
      () => {
        const pcmSource = preDecodedPcm ?? opusPcmForWaveform;
        if (pcmSource) {
          return calculateWaveform(pcmSource);
        }

        throw new Error(`Region ${id} has no decoded audio to calculate a waveform from`);
      },
    );

    return { audioBlob: blob, id, duration, preDecodedPcm, waveform };
  }

  /**
   * Load multiple regions concurrently.
   *
   * Progress callback is debounced internally via `queueMicrotask` —
   * multiple regions resolving on the same microtask tick produce
   * a single `onProgress` call.
   *
   * Failed regions are reported via `onError` and excluded from results.
   * Successful regions are always returned, even if some fail.
   */
  async loadAll(
    regions: RegionLoadInput[],
    onProgress?: (loaded: number, total: number) => void,
    onError?: (regionId: string, error: Error) => void,
  ): Promise<RegionLoadResult[]> {
    const total = regions.length;
    let loaded = 0;
    let progressScheduled = false;

    const notifyProgress = () => {
      if (onProgress && !progressScheduled) {
        progressScheduled = true;
        queueMicrotask(() => {
          progressScheduled = false;
          onProgress(loaded, total);
        });
      }
    };

    const settled = await Promise.allSettled(
      regions.map(async (region) => {
        const result = await this.loadFromKey(
          region.id,
          region.key,
          region.waveformKey,
          region.audioBlob,
          {
            codec: region.codec,
            duration: region.duration,
          }
        );
        loaded++;
        notifyProgress();
        return result;
      })
    );

    const results: RegionLoadResult[] = [];
    for (let i = 0; i < settled.length; i++) {
      const outcome = settled[i];
      if (outcome.status === "fulfilled") {
        results.push(outcome.value);
      } else {
        const error = outcome.reason instanceof Error
          ? outcome.reason
          : new Error(String(outcome.reason));
        onError?.(regions[i].id, error);
        loaded++;
        notifyProgress();
      }
    }

    return results;
  }

  /**
   * Generate waveform data from an existing AudioBuffer.
   * Used for recordings and punch-in operations where the buffer already exists.
   */
  static calculateWaveform(audioBuffer: AudioBuffer): WaveformData {
    return calculateWaveform(audioBuffer);
  }
}
